/*  v3.11 · Shared mix sort helper
 *  ---------------------------------------------------------
 *  Sorts mix designs into the way batchers scan a real ready-mix board:
 *  every 25 MPA together (ascending by aggregate/slump), then every 32,
 *  then every 40, then every 50. Special mixes (blockfill, sand, ice
 *  rock) go after all MPA lines. EXPOSED aggregates always sit at the
 *  very bottom because they're the least-common request.
 *
 *  MPA strength is parsed out of `mix.code` first, `mix.name` second.
 *  Handles "25MPA NEUTRAL", "32/20 WINTER SLAB", "40 MPA TANK" and
 *  legacy short codes like "N25", "N32", "N40" plus "S25", "S32".
 * ------------------------------------------------------- */

const MPA_RX = /(?:^|[^\d])([2-6]\d)(?:MPA|\s*MPA|\/\d)/i;
const N_SHORT_RX = /^[NSA]?([2-6]\d)\b/i;  // e.g. "N32", "S25", "A50"

export function parseMpa(mix) {
  const src = `${mix?.code || ""} ${mix?.name || ""}`;
  let m = src.match(MPA_RX);
  if (m) return parseInt(m[1], 10);
  m = (mix?.code || "").match(N_SHORT_RX);
  if (m) return parseInt(m[1], 10);
  return null;   // non-MPA (blockfill, sand, ice rock, etc.)
}

export function isExposed(mix) {
  const src = `${mix?.code || ""} ${mix?.name || ""}`;
  return /\bEXPOSED?\b/i.test(src);
}

/**
 * Sort into 3 buckets, preserving stability inside each bucket:
 *   1. MPA mixes → grouped by MPA ascending, then by code
 *   2. Special mixes (blockfill / sand / ice rock / etc.) → alpha by code
 *   3. Exposed aggregate mixes → alpha by code (always LAST)
 */
export function sortMixes(mixes) {
  const bucket = (m) => {
    if (isExposed(m)) return 3;
    if (parseMpa(m) != null) return 1;
    return 2;
  };
  return [...(mixes || [])].sort((a, b) => {
    const ba = bucket(a), bb = bucket(b);
    if (ba !== bb) return ba - bb;
    if (ba === 1) {
      const ma = parseMpa(a) ?? 999, mb = parseMpa(b) ?? 999;
      if (ma !== mb) return ma - mb;
    }
    return String(a.code || a.name || "").localeCompare(String(b.code || b.name || ""));
  });
}
